import ContactSupportRoundedIcon from '@mui/icons-material/ContactSupportRounded';
import {
  Alert,
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CoursesApi from '../../apis/CoursesApi';
import ContactUsDialog from '../../components/ContactUsDialog/ContactUsDialog';
import LoadingCircle from '../../components/LoadingCircle/LoadingCircle';
import MobileDrawerToggle from '../../components/MobileDrawerToggle/MobileDrawerToggle';
import SnackbarContext from '../../contexts/SnackbarContext';
import { CourseModuleDto } from '../../generated-types/courseModuleDto';
import { CourseModuleTypeDto } from '../../generated-types/courseModuleTypeDto';
import useApi from '../../lib/useApi';
import useFooterControl from '../../lib/useFooterControl';
import BackButton from './components/BackButton/BackButton';
import CourseNavButton from './components/CourseNavButton/CourseNavButton';
import CompleteModule from './components/modules/CompleteModule/CompleteModule';
import IntroModule from './components/modules/IntroModule/IntroModule';
import QuizModule from './components/modules/QuizModule/QuizModule';
import SurveyModule from './components/modules/SurveyModule/SurveyModule';
import VideoModule from './components/modules/VideoModule/VideoModule';
import NextButton from './components/NextButton/NextButton';
import VideoSkippedDialog from './components/VideoSkippedDialog';
import VideoSkippedWarningIcon from './components/VideoSkippedWarningIcon';

/**
 * This is the primary view for displaying a course
 */
const CourseView = () => {
  const { courseSlug } = useParams();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isModuleLoading, setIsModuleLoading] = useState(false);
  const [showVideoSkipDialog, setShowVideoSkipDialog] = useState(false);
  const [videoSkipDetected, setVideoSkipDetected] = useState(false);
  const [videoTracking, setVideoTracking] = useState<boolean[]>([]);
  const [videoDuration, setVideoDuration] = useState(0);
  const [courseModules, setCourseModules] = useState<CourseModuleDto[]>([]);
  const [courseId, setCourseId] = useState('');
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedCourseModuleIndex, setSelectedCourseModuleIndex] =
    useState<number>(-1);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const coursesApi = useApi(CoursesApi);
  const snackbarContext = useContext(SnackbarContext);
  useFooterControl(false);

  // We maintain the complete state separately outside course modules to ensure we don't improperly rerender
  //  when the complete state is updated locally.
  const [moduleCompleteState, setModuleCompleteState] = useState<boolean[]>([]);

  /**
   * Set the module complete, by it's index
   */
  const setModuleAsComplete = useCallback(
    (index: number) => {
      // Complete the module if needed
      if (!moduleCompleteState[index]) {
        setIsModuleLoading(true);
        coursesApi
          .CompleteModule(courseModules[index].moduleId)
          .then(() => {
            // Set it complete locally
            setModuleCompleteState((c) => {
              c[index] = true;
              return [...c];
            });
          })
          .catch(() => {
            snackbarContext.setAlert(
              <Alert variant="filled" severity="warning">
                Failed to complete the module. Please try again. If this
                continues, contact us!
              </Alert>
            );
          })
          .finally(() => setIsModuleLoading(false));
      }
    },
    [courseModules, coursesApi, moduleCompleteState, snackbarContext]
  );

  /**
   * Move to the next module in the course
   */
  const moveToNextModule = useCallback(
    () => setSelectedCourseModuleIndex((i) => i + 1),
    []
  );

  /**
   * The jsx elements that represent the course
   */
  const courseModuleElements: JSX.Element[] = useMemo(
    () =>
      courseModules.map((m, i) => {
        switch (m.moduleType) {
          case CourseModuleTypeDto.Quiz:
            return (
              <QuizModule
                moduleId={m.moduleId}
                setIsLoading={setIsModuleLoading}
                setComplete={() => setModuleAsComplete(i)}
                nextModule={moveToNextModule}
              />
            );
          case CourseModuleTypeDto.Video:
            return (
              <VideoModule
                moduleId={m.moduleId}
                setIsLoading={setIsModuleLoading}
                setComplete={() => setModuleAsComplete(i)}
                nextModule={moveToNextModule}
                setVideoSkipDetected={(skipped, videoTracking, duration) => {
                  setVideoSkipDetected(skipped);
                  setVideoTracking(videoTracking);
                  setVideoDuration(duration);
                }}
              />
            );
          case CourseModuleTypeDto.Introduction:
            return (
              <IntroModule
                setIsLoading={setIsModuleLoading}
                moduleId={m.moduleId}
                setComplete={() => setModuleAsComplete(i)}
                nextModule={moveToNextModule}
              />
            );
          case CourseModuleTypeDto.Complete:
            return (
              <CompleteModule
                courseId={courseId}
                setIsLoading={setIsModuleLoading}
                moduleId={m.moduleId}
                setComplete={() => setModuleAsComplete(i)}
                nextModule={moveToNextModule}
              />
            );
          case CourseModuleTypeDto.Survey:
            return (
              <SurveyModule
                setIsLoading={setIsModuleLoading}
                moduleId={m.moduleId}
                setComplete={() => setModuleAsComplete(i)}
                nextModule={moveToNextModule}
              />
            );
          default:
            return <></>;
        }
      }),
    [courseId, courseModules, moveToNextModule, setModuleAsComplete]
  );

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    if (courseSlug) {
      const init = async () => {
        // Find the course id
        const retrievedCourseId = await coursesApi.GetCourseId(courseSlug);
        setCourseId(retrievedCourseId);

        // Get the modules for the course
        const databaseModules = await coursesApi.GetModules(retrievedCourseId);
        const orderedModules = databaseModules.sort(
          (a, b) => a.order - b.order
        );

        // Figure out the current module
        let foundInitialIndex = 0;
        // - If the last module is not complete, we may assume the course is in progress
        if (!orderedModules[orderedModules.length - 1].complete) {
          // Loop through the modules until we encounter the first incomplete module
          for (let i = 0; i < orderedModules.length; i++) {
            const courseModule = orderedModules[i];
            if (!courseModule.complete) {
              foundInitialIndex = i;
              break;
            }
          }
        }

        // Complete the loading
        if (active) {
          setCourseModules(orderedModules);
          setModuleCompleteState(orderedModules.map((m) => m.complete));
          setSelectedCourseModuleIndex(foundInitialIndex);
          setIsLoading(false);
        }
      };
      init();
    }

    return () => {
      active = false;
    };
  }, [courseSlug, coursesApi]);

  useEffect(() => {
    // Once the course modules are set and an initial course module is selected,
    //  then, we can turn the loading off
    if (courseModules.length > 0 && selectedCourseModuleIndex >= 0) {
      setIsLoading(false);
    }
  }, [courseModules.length, selectedCourseModuleIndex, setIsLoading]);

  useEffect(() => {
    setVideoSkipDetected(false);
    setVideoTracking([]);
    setVideoDuration(0);
    setShowVideoSkipDialog(false);
  }, [selectedCourseModuleIndex]);

  /**
   * The width of the navigation buttons
   */
  const buttonWidth = useMemo(() => (isMd ? '8em' : '35%'), [isMd]);

  /**
   * A button to move to the next module
   */
  const nextButton = useMemo(
    () => (
      <NextButton
        width={buttonWidth}
        completeStates={moduleCompleteState}
        selectedCourseModuleIndex={selectedCourseModuleIndex}
        courseModules={courseModules}
        moveToNextModule={moveToNextModule}
      />
    ),
    [
      buttonWidth,
      courseModules,
      moduleCompleteState,
      moveToNextModule,
      selectedCourseModuleIndex,
    ]
  );

  /**
   * Navigate backwards through the course using this button
   */
  const backButton = useMemo(
    () => (
      <BackButton
        completeStates={moduleCompleteState}
        width={buttonWidth}
        selectedCourseModuleIndex={selectedCourseModuleIndex}
        courseModules={courseModules}
        moveToPreviousModule={() => setSelectedCourseModuleIndex((i) => i - 1)}
      />
    ),
    [buttonWidth, courseModules, moduleCompleteState, selectedCourseModuleIndex]
  );

  /**
   * Determine whether a course module should be disabled
   */
  const shouldCourseModuleBeDisabled = useCallback(
    (index: number) => {
      let previousState = false;

      // If we're not on the first index, then move the index back one to
      //  get the previous module's completion state
      if (index - 1 >= 0) {
        previousState = !moduleCompleteState[index - 1];
      }

      return previousState;
    },
    [moduleCompleteState]
  );

  /**
   * Prepend the module number
   */
  const prependModuleNumber = useCallback(
    (moduleNumber: number, text: string) => `Module ${moduleNumber} ${text}`,
    []
  );

  /**
   * Get the subtitle to use in the course module navigation
   */
  const getSubtitle = useCallback(
    (courseModuleTypeDto: CourseModuleTypeDto, moduleNumber?: number) => {
      // Only quiz and video modules have subtitles. In both cases, we always expect the module number to be defined.
      if (courseModuleTypeDto == CourseModuleTypeDto.Quiz)
        return prependModuleNumber(moduleNumber!, 'Quiz');
      if (courseModuleTypeDto == CourseModuleTypeDto.Video)
        return prependModuleNumber(moduleNumber!, 'Video');

      // All other use cases result in no subtitle
      return undefined;
    },
    [prependModuleNumber]
  );

  /**
   * Navigation used in the side bar (or drawer on small screens) for the course
   */
  const courseNav = useMemo(
    () => (
      <Box
        display="flex"
        padding="1em"
        gap="1em"
        width="100%"
        minWidth="15em"
        flexDirection="column"
        overflow="auto"
      >
        {courseModules.map((c, i) => (
          <CourseNavButton
            key={i}
            complete={moduleCompleteState[i]}
            disabled={shouldCourseModuleBeDisabled(i)}
            title={c.title}
            subtitle={getSubtitle(c.moduleType, c.moduleNumber)}
            onClick={() => {
              setSelectedCourseModuleIndex(i);
              setShowMobileDrawer(false);
            }}
            selected={selectedCourseModuleIndex == i}
            mobileSize={!isMd}
          />
        ))}
      </Box>
    ),
    [
      courseModules,
      getSubtitle,
      isMd,
      moduleCompleteState,
      selectedCourseModuleIndex,
      shouldCourseModuleBeDisabled,
    ]
  );

  return (
    <Box height="100%" width="100%" display="flex">
      {!isLoading && (
        <Box
          display="flex"
          flexDirection={isMd ? 'row' : 'column'}
          height="100%"
          width="100%"
        >
          {isMd && (
            <Box display="flex">
              {courseNav}
              <Divider orientation="vertical" flexItem />
            </Box>
          )}
          <Box
            position="relative"
            height="100%"
            width="100%"
            display="flex"
            overflow="auto"
            flexDirection={isMd ? 'column' : 'column-reverse'}
          >
            <Box height="100%" overflow="auto">
              {courseModuleElements[selectedCourseModuleIndex]}
            </Box>
            <Divider flexItem />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              padding=".5em"
            >
              <Box>
                <Typography fontSize="1.5em">
                  {courseModules[selectedCourseModuleIndex].title}
                </Typography>
                <Typography
                  fontSize=".7em"
                  color={theme.palette.text.secondary}
                >
                  © {new Date().getFullYear()}, Strategic Security Solutions
                  Group LLC. All rights reserved.
                </Typography>
              </Box>
              <Box display="flex" gap=".5em" alignItems="center">
                <VideoSkippedWarningIcon
                  moduleComplete={
                    selectedCourseModuleIndex >= 0
                      ? courseModules[selectedCourseModuleIndex].complete
                      : false
                  }
                  setShowDialogVideoSkipped={() => setShowVideoSkipDialog(true)}
                  videoSkipped={videoSkipDetected}
                />
                <Tooltip title="Contact Support" arrow>
                  <IconButton onClick={() => setShowContactDialog(true)}>
                    <ContactSupportRoundedIcon />
                  </IconButton>
                </Tooltip>
                {isMd && (
                  <Box display="flex" gap="1em">
                    {backButton}
                    {nextButton}
                  </Box>
                )}
              </Box>
            </Box>
            <LoadingCircle isLoading={isModuleLoading} />
          </Box>
          <Drawer
            variant="temporary"
            anchor="bottom"
            open={showMobileDrawer}
            sx={{
              zIndex: 1500,
            }}
          >
            {courseNav}
            <MobileDrawerToggle
              drawerState="showing"
              setShowNav={() => setShowMobileDrawer(false)}
            />
          </Drawer>
          <MobileDrawerToggle
            drawerState="hidden"
            setShowNav={() => setShowMobileDrawer(true)}
            leftSlot={backButton}
            rightSlot={nextButton}
          />
        </Box>
      )}
      <LoadingCircle isLoading={isLoading} loadingText="Loading Course..." />
      <VideoSkippedDialog
        open={showVideoSkipDialog}
        setOpen={setShowVideoSkipDialog}
        videoTracking={videoTracking}
        duration={videoDuration}
      />
      <ContactUsDialog
        open={showContactDialog}
        onClose={() => setShowContactDialog(false)}
      />
    </Box>
  );
};

export default CourseView;
